import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';

import { CalorieIntakeBaseline } from '@cf-shared/data-model/log-weekly-checkup';
import { CalorieChangeDirection, calorieChangeFromCalories, isPositiveNumber } from 'components/cs-calorie-change-input/calorie-change';
import { CalorieChangeAudience } from 'components/cs-calorie-change-input/cs-calorie-change-input';
import { CsCardComponent, CsCardInterfaceClass } from 'components/cs-card/cs-card';
import { CsCardListInterfaceClass } from 'components/cs-card-list/cs-card-list-interface';
import { LL } from 'i18n/i18n.service';
import type { TranslatedString } from 'models/translated-string';
import { numberText } from 'services/body-comp-overview-model';
import { reportedIntakeSourceText } from './next-intake-source';

/**
 * "Next week's intake" (#5456 review round): the weekly check-up's calorie decision condensed into
 * one card, the way the coach's mock-up draws it. Since #6676 it is a card of its own (a wrapper
 * around cs-card), so a host puts it straight into its card list. Used by the coach's Decision tab and by a
 * person's own last check-up step, so both look the same.
 *
 * Example, BORI, who reported his logged average: "now 2178 kcal/day", "Average daily intake last
 * week (logged)",
 * "Recommended: −109 kcal/day (−5.0%) → 2069 kcal/day", and under it the host's radio rows.
 *
 * It draws the figures only. The answers are the host's own radio rows, projected into the card,
 * because the page's bar already holds Back and Confirm: buttons in the card would compete with
 * them (owner, 2026-10-27). See design-docs/coaching-dashboard.md, "Next week's intake".
 */
@Component({
  selector: 'cs-next-intake-card',
  templateUrl: 'cs-next-intake-card.html',
  standalone: false,
  providers: [{ provide: CsCardInterfaceClass, useExisting: CsNextIntakeCardComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsNextIntakeCardComponent extends CsCardInterfaceClass implements AfterViewInit {
  protected LL = LL;
  @ViewChild(CsCardComponent) card: CsCardComponent;

  /**
   * the intake the person reported at the check-up. It is last week's logged average only when
   * they picked that; it can also be their calorie target or a number they typed in
   */
  @Input() nowKcal: number | undefined;
  /** how that intake was chosen; the line under the figure names it, so it never claims an average that is a target */
  @Input() nowSource: CalorieIntakeBaseline | undefined;
  /** who reads the card: "Your calorie target" for the person, "Their calorie target" for a coach */
  @Input() audience: CalorieChangeAudience = CalorieChangeAudience.Self;
  /** the app's number for next week */
  @Input() recommendedKcal: number | undefined;
  /** false when the app offers no change: the line then says it keeps its target */
  @Input() recommendsChange = true;

  constructor(elemRef: ElementRef) {
    super(elemRef);
  }

  ngAfterViewInit(): void {
    // the list hands itself to this host before the inner card exists, so pass it on now
    this.card?.setList(this.list);
    this.card?.updateLayout();
  }

  // forward the list to the inner card too: its own height changes (the host's target field
  // opening under its radio row) re-stack the list only through the list it holds
  override setList(list: CsCardListInterfaceClass): void {
    super.setList(list);
    this.card?.setList(list);
  }

  // the card list's layout calls go to the inner cs-card, which draws the card
  override updateLayout(): void {
    this.card?.updateLayout();
  }

  override getCurrentCardHeight(): number {
    return this.card ? this.card.getCurrentCardHeight() : 0;
  }

  /** the two figures as whole kcal, also put on the page as data-kcal, so a test can read them without parsing text */
  protected get roundedNowKcal(): number | undefined {
    return isPositiveNumber(this.nowKcal) ? Math.round(this.nowKcal) : undefined;
  }

  protected get roundedRecommendedKcal(): number | undefined {
    return isPositiveNumber(this.recommendedKcal) ? Math.round(this.recommendedKcal) : undefined;
  }

  protected get nowText(): TranslatedString | undefined {
    return isPositiveNumber(this.nowKcal) ? LL.nextIntake.now({ kcal: Math.round(this.nowKcal) }) : undefined;
  }

  /** what the figure is, by how it was chosen: an average only when it really is one */
  protected get nowSourceLine(): TranslatedString | undefined {
    if (!isPositiveNumber(this.nowKcal)) return undefined;
    return reportedIntakeSourceText(this.nowSource, this.audience === CalorieChangeAudience.Coach, LL);
  }

  /** "Recommended: −109 kcal/day (−5.0%) → 2069 kcal/day", or "Recommended: keep 2178 kcal/day" */
  protected get recommendedText(): TranslatedString | undefined {
    if (!isPositiveNumber(this.recommendedKcal)) return undefined;
    const kcal = Math.round(this.recommendedKcal);
    if (!this.recommendsChange || !isPositiveNumber(this.nowKcal)) {
      return LL.nextIntake.recommendedKeep({ kcal });
    }
    const delta = numberText(kcal - Math.round(this.nowKcal), 0, true);
    const change = calorieChangeFromCalories(this.nowKcal, this.recommendedKcal);
    if (!change) {
      return LL.nextIntake.recommendedChangeNoPercent({ change: delta, kcal });
    }
    const signedPercent = change.direction === CalorieChangeDirection.Decrease ? -change.percent : change.percent;
    return LL.nextIntake.recommendedChange({ change: delta, percent: numberText(signedPercent, 1, true), kcal });
  }
}
